import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';

// Initialize mermaid once
let mermaidInitialized = false;
const initMermaid = () => {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    themeVariables: {
      primaryColor: '#6366f1',
      primaryTextColor: '#fff',
      primaryBorderColor: '#6366f1',
      lineColor: '#6366f1',
      secondaryColor: '#0a0a0a',
      tertiaryColor: '#171717',
      background: '#0a0a0a',
      mainBkg: '#171717',
      nodeBorder: '#6366f1',
      clusterBkg: '#0a0a0a',
      titleColor: '#fff',
      edgeLabelBackground: '#0a0a0a',
    },
    securityLevel: 'loose',
    fontFamily: 'JetBrains Mono, monospace',
    flowchart: { useMaxWidth: true, htmlLabels: true },
    er: { useMaxWidth: true },
    sequence: { useMaxWidth: true },
  });
  mermaidInitialized = true;
};

let mermaidCounter = 0;

export default function MermaidDiagram({ chart }) {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const idRef = useRef(`mermaid-${++mermaidCounter}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const renderChart = useCallback(async () => {
    if (!chart) return;
    
    initMermaid();
    
    // Clean up any existing element with our ID
    const existingEl = document.getElementById(idRef.current);
    if (existingEl) existingEl.remove();
    
    // Generate a fresh ID for this render
    const renderId = `mermaid-render-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      setError(null);
      
      // Clean the chart - remove extra whitespace and fix common issues
      const cleanedChart = chart
        .trim()
        .replace(/^\s+/gm, '') // Remove leading whitespace from each line
        .replace(/\n{3,}/g, '\n\n'); // Reduce multiple newlines
      
      const { svg: renderedSvg } = await mermaid.render(renderId, cleanedChart);
      setSvg(renderedSvg);
    } catch (err) {
      console.warn('Mermaid render error:', err.message);
      setError(err.message || 'Failed to render diagram');
      // Clean up any leftover elements
      const el = document.getElementById(renderId);
      if (el) el.remove();
      
      // Retry once after a short delay
      if (retryCount < 1) {
        setTimeout(() => setRetryCount(prev => prev + 1), 500);
      }
    }
  }, [chart, retryCount]);

  useEffect(() => {
    renderChart();
  }, [renderChart]);

  if (error) {
    return (
      <div className="bg-red-500/5 border border-red-500/20 rounded-md p-3 my-2" data-testid="mermaid-error">
        <p className="text-[10px] text-red-400 mb-1">Diagram render error</p>
        <pre className="text-[10px] opacity-50 font-mono whitespace-pre-wrap">{chart}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-4 my-2 text-center">
        <div className="w-4 h-4 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-3 my-2 overflow-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
      data-testid="mermaid-diagram"
    />
  );
}
